import { FolderClosed, MoreHorizontal, Trash2 } from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { ROW_CHECKBOX_HIT_AREA } from '@/components/documents/document-grid'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { GridTableRow } from '@/components/ui/grid-table'
import type { FolderTreeFolder } from '@/features/documents/types'
import type { CaseDocumentStatusFilter } from '@/services/api/cases'

export interface FolderRowProps {
  folder: FolderTreeFolder
  isSelected: boolean
  index: number
  onToggleSelect: (index: number) => void
  onNavigate: (name: string) => void
  onDelete?: (folderPath: string, fileCount: number) => void
  activeStatus?: CaseDocumentStatusFilter
}

export const FolderRow = React.memo(function FolderRow({
  folder,
  isSelected,
  index,
  onToggleSelect,
  onNavigate,
  onDelete,
  activeStatus,
}: FolderRowProps) {
  const filteredCount = activeStatus
    ? (folder.file_count_by_status?.[activeStatus] ?? 0)
    : folder.file_count
  const dim = activeStatus !== undefined && filteredCount === 0
  const { t } = useTranslation()
  // A folder holding HUMINT captures is not deleted: they are immutable evidence (ADR-0045).
  const captureCount = folder.capture_count ?? 0
  return (
    <GridTableRow
      selected={isSelected}
      className={dim ? 'opacity-50' : undefined}
      onClick={() => onNavigate(folder.name)}
    >
      {/* The whole cell toggles: on the phone it is padded out to a row-high tap target. */}
      <div
        className={ROW_CHECKBOX_HIT_AREA}
        onClick={e => {
          e.stopPropagation()
          onToggleSelect(index)
        }}
      >
        <Checkbox checked={isSelected} />
      </div>
      <div className="flex items-center gap-2 text-sm text-foreground font-medium truncate">
        <FolderClosed className="size-4 text-warning shrink-0" />
        <span className="truncate">{folder.name}</span>
        <span className="text-xs text-muted-foreground font-normal shrink-0">
          {activeStatus
            ? `${filteredCount} ${activeStatus}`
            : `${folder.file_count} ${folder.file_count === 1 ? 'file' : 'files'}`}
        </span>
      </div>
      <div className="text-xs text-muted-foreground max-md:hidden">Folder</div>
      <div className="text-xs text-muted-foreground max-md:hidden" />
      <div className="max-md:hidden" />
      <div className="max-md:hidden" />
      <div className="max-md:hidden" />
      <div className="max-md:hidden" />
      <div className="opacity-0 group-hover:opacity-100 transition-opacity max-md:opacity-100">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-8 md:size-6"
              onClick={e => e.stopPropagation()}
            >
              <MoreHorizontal className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className={captureCount > 0 ? 'w-64' : undefined}>
            <DropdownMenuItem
              className="text-destructive"
              disabled={captureCount > 0}
              onClick={() => onDelete?.(folder.path, folder.file_count)}
            >
              <Trash2 className="size-4 mr-2" /> Delete Folder
            </DropdownMenuItem>
            {captureCount > 0 && (
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                {t('documents.capture.folderLocked', { count: captureCount })}
              </DropdownMenuLabel>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </GridTableRow>
  )
})
